import React, { useMemo } from "react";
import closeIcon from "@src/assets/svg/close-icon.svg";
import styles from "./tickets-taskbar-card.module.scss";
import { useClickOnCardGetTicketById } from "./use-click-on-card-get-ticket-by-id";
import { SmallSpinner } from "@src/components/atoms/small-spinner/small-spinner";
import { TaskbarTicketCardSpinner } from "@src/components/atoms/taskbar-ticket-card-spinner/taskbar-ticket-card-spinner";
import { useI18N } from "@src/language/language-context";
import { SupportAgentTicket } from "@src/types/support";
import { CustomTooltip } from "@src/components/atoms/custom-tooltip/custom-tooltip";

const COLORS = [
  "rgba(147, 255, 153, 0.25)",
  "rgba(255, 202, 147, 0.25)",
  "rgba(147, 200, 255, 0.25)",
  "rgba(224, 147, 255, 0.25)",
  "rgba(255, 147, 200, 0.25)",
  "rgba(58, 172, 200, 0.25)",
];

interface TicketsTaskbarCardPropsTypes {
  ticket: SupportAgentTicket;
  patchLoading?: boolean;
  handleCloseIconClick: (
    e: React.MouseEvent<HTMLButtonElement>,
    ticketId: string,
  ) => void;
  isMarkAsDoneDisabled?: boolean;
  disabledMarkAsDoneTooltip?: string;
}

const cssClassName = "tickets-taskbar-card";

export const TicketsTaskbarCard = ({
  ticket,
  patchLoading = false,
  handleCloseIconClick,
  isMarkAsDoneDisabled = false,
  disabledMarkAsDoneTooltip = "",
}: TicketsTaskbarCardPropsTypes) => {
  const i18n = useI18N();
  const { handleCardClick, isLoading } = useClickOnCardGetTicketById();

  const texts = {
    altCloseIcon: i18n("modules.ticketsTaskbarCard.altCloseIcon"),
  };

  const randomColor = useMemo(
    () => COLORS[Math.floor(Math.random() * COLORS.length)],
    [],
  );

  const nameParts = ticket.raised_by.name.split(" ");
  const initials =
    nameParts.length === 1
      ? nameParts[0].slice(0, 2).toUpperCase()
      : nameParts
          .slice(0, 2)
          .map((part) => part[0])
          .join("")
          .toUpperCase();

  const closeButton = (
    <button
      className={`${styles[`${cssClassName}__icon-button`]} ${
        isMarkAsDoneDisabled
          ? styles[`${cssClassName}__icon-button--disabled`]
          : ""
      }`}
      onClick={(e) => handleCloseIconClick(e, ticket.id)}
      disabled={isMarkAsDoneDisabled}
    >
      <img src={closeIcon} alt={texts.altCloseIcon} />
    </button>
  );

  const cancelTooltip = isMarkAsDoneDisabled ? disabledMarkAsDoneTooltip : "";

  return (
    <div className={styles[cssClassName]}>
      <div className={styles[`${cssClassName}__inner`]}>
        {isLoading ? (
          <div className={styles[`${cssClassName}__spinner-wrapper`]}>
            <SmallSpinner />
          </div>
        ) : (
          <button
            className={styles[`${cssClassName}__card`]}
            onClick={() => handleCardClick(ticket.id)}
          >
            <div
              className={styles[`${cssClassName}__avatar`]}
              style={{ background: randomColor }}
            >
              <span className={styles[`${cssClassName}__avatar-text`]}>
                {initials}
              </span>
            </div>
            <div className={styles[`${cssClassName}__text-block`]}>
              <span className={styles[`${cssClassName}__ticket-title`]}>
                {ticket.title}
              </span>
              <span className={styles[`${cssClassName}__description`]}>
                {ticket.description}
              </span>
            </div>
          </button>
        )}

        <div className={styles[`${cssClassName}__icon`]}>
          {patchLoading ? (
            <TaskbarTicketCardSpinner />
          ) : cancelTooltip ? (
            <CustomTooltip content={cancelTooltip}>{closeButton}</CustomTooltip>
          ) : (
            closeButton
          )}
        </div>
      </div>
    </div>
  );
};
